import React, { useState, useEffect, useRef } from 'react';
import { X, Code } from 'lucide-react';
import { createSnippet, updateSnippet, Snippet } from '../lib/snippets';
import { SNIPPET_CUSTOMER_VARIABLES, SNIPPET_LEAD_VARIABLES } from '../lib/snippetVariables';
import { useModalZIndex } from '../contexts/ModalStackContext';

interface CreateSnippetModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (snippet: Snippet) => void;
  businessId: string;
  editSnippet?: Snippet | null;
}

export default function CreateSnippetModal({
  isOpen,
  onClose,
  onSuccess,
  businessId,
  editSnippet
}: CreateSnippetModalProps) {
  const modalZ = useModalZIndex(100080);
  const [isAnimating, setIsAnimating] = useState(false);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const contentTextareaRef = useRef<HTMLTextAreaElement>(null);

  // Available variables that can be inserted
  const variables = SNIPPET_CUSTOMER_VARIABLES;
  const leadVariables = SNIPPET_LEAD_VARIABLES;

  // Insert variable at cursor position
  const insertVariable = (variable: string) => {
    const textarea = contentTextareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = content;
    const before = text.substring(0, start);
    const after = text.substring(end);
    
    const newText = before + variable + after;
    setContent(newText);
    
    // Set cursor position after the inserted variable
    setTimeout(() => {
      textarea.focus();
      const newPosition = start + variable.length;
      textarea.setSelectionRange(newPosition, newPosition);
    }, 0);
  };

  // Populate fields when editing
  useEffect(() => {
    if (editSnippet) {
      setTitle(editSnippet.title);
      setContent(editSnippet.content);
    } else {
      setTitle('');
      setContent('');
    }
    setError(null);
  }, [editSnippet, isOpen]);

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
    }
  }, [isOpen]);

  const handleTransitionEnd = () => {
    if (!isOpen) {
      setIsAnimating(false);
      setTitle('');
      setContent('');
      setError(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!title.trim() || !content.trim()) {
      setError('Title and content are required');
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      let snippet: Snippet;
      
      if (editSnippet) {
        // Update existing snippet
        snippet = await updateSnippet(editSnippet.id, {
          title: title.trim(),
          content: content.trim()
        });
      } else {
        // Create new snippet
        snippet = await createSnippet(businessId, title.trim(), content.trim());
      }
      
      onSuccess(snippet);
      onClose();
    } catch (err) {
      console.error('Error saving snippet:', err);
      setError(err instanceof Error ? err.message : 'Failed to save snippet');
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen && !isAnimating) return null;

  const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;

  return (
    <div 
      className={`fixed inset-0 transition-opacity duration-200 ${
        isOpen ? 'bg-black/50' : 'bg-black/0'
      }`}
      style={{ zIndex: modalZ, backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)' }}
      onClick={onClose}
    >
      <div 
        className={`bg-gray-50 transform transition-all duration-200 ${
          isMobile
            ? `relative w-full h-full ${isOpen ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'}`
            : `fixed inset-y-0 right-0 w-full md:w-[600px] shadow-2xl ${isOpen ? 'translate-x-0' : 'translate-x-full'}`
        }`}
        style={{
          zIndex: modalZ,
          fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
          paddingTop: 'env(safe-area-inset-top, 0px)',
          paddingBottom: 'env(safe-area-inset-bottom, 0px)',
          paddingLeft: 'env(safe-area-inset-left, 0px)',
          paddingRight: 'env(safe-area-inset-right, 0px)',
        }}
        onTransitionEnd={handleTransitionEnd}
        onClick={(e) => e.stopPropagation()}
      >
        <form onSubmit={handleSubmit} className="h-full flex flex-col overflow-hidden">
          {/* Header */}
          <div className="bg-white border-b border-gray-100 px-4 py-3 flex items-center gap-3">
            <button
              type="button"
              onClick={onClose}
              className="h-9 w-9 flex items-center justify-center text-gray-500 hover:text-gray-700 rounded-full hover:bg-gray-100 active:bg-gray-200 active:scale-95 transition-all duration-150"
              style={{ WebkitTapHighlightColor: 'transparent' }}
            >
              <X className="h-5 w-5" />
            </button>
            <h2 className="flex-1 text-lg font-bold text-gray-900" style={{ letterSpacing: '-0.02em' }}>
              {editSnippet ? 'Edit Snippet' : 'Create Snippet'}
            </h2>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-4">
            <div className="space-y-5">
              {error && (
                <div className="bg-red-50 border border-red-100 rounded-xl p-4">
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              )}

              {/* Title Field */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="e.g., Appointment Confirmation"
                  className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all"
                  disabled={isSaving}
                  maxLength={100}
                />
                <p className="text-xs text-gray-500 mt-1.5 px-1">
                  Short descriptive name for this snippet
                </p>
              </div>

              {/* Content Field */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Message Content <span className="text-red-500">*</span>
                </label>
                
                {/* Variable Picker */}
                <div className="mb-3 p-3.5 bg-white border border-gray-200 rounded-xl">
                  <div className="flex items-center gap-2 mb-2.5">
                    <Code className="h-4 w-4 text-gray-500" />
                    <span className="text-xs font-semibold text-gray-700">Insert Variable</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {variables.map((variable) => (
                      <button
                        key={variable.value}
                        type="button"
                        onClick={() => insertVariable(variable.value)}
                        className="px-3 py-1.5 text-xs font-semibold text-blue-700 bg-blue-50 border border-blue-100 rounded-lg hover:bg-blue-100 active:bg-blue-200 active:scale-95 transition-all"
                        style={{ WebkitTapHighlightColor: 'transparent' }}
                        disabled={isSaving}
                      >
                        {variable.label}
                      </button>
                    ))}
                  </div>
                  <div className="flex items-center gap-2 mt-3 mb-2.5">
                    <Code className="h-4 w-4 text-gray-500" />
                    <span className="text-xs font-semibold text-gray-700">Lead Variables</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {leadVariables.map((variable) => (
                      <button
                        key={variable.value}
                        type="button"
                        onClick={() => insertVariable(variable.value)}
                        className="px-3 py-1.5 text-xs font-semibold text-purple-700 bg-purple-50 border border-purple-100 rounded-lg hover:bg-purple-100 active:bg-purple-200 active:scale-95 transition-all"
                        style={{ WebkitTapHighlightColor: 'transparent' }}
                        disabled={isSaving}
                      >
                        {variable.label}
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 mt-2.5">
                    Variables are replaced with real data when inserted. Lead variables only fill when the customer has an open lead in CRM — otherwise they're left blank and you'll see a warning.
                  </p>
                </div>

                <textarea
                  ref={contentTextareaRef}
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  placeholder="Type your message here..."
                  rows={10}
                  className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 resize-none font-mono text-sm transition-all"
                  disabled={isSaving}
                />
                <p className="text-xs text-gray-500 mt-1.5 px-1">
                  {'Example: "Hi {{firstName}}, your appointment is confirmed!"'}
                </p>
              </div>

              {/* Helper Text */}
              <div className="bg-blue-50 border border-blue-100 rounded-xl p-4">
                <h4 className="text-sm font-semibold text-blue-900 mb-2">Tips</h4>
                <ul className="text-sm text-blue-700/90 space-y-1 list-disc list-inside">
                  <li>Use variables to personalize messages automatically</li>
                  <li>{'Example: "Hi {{firstName}}, your appointment is tomorrow!"'}</li>
                  <li>Create snippets for confirmations, reminders, and FAQs</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="bg-white border-t border-gray-100 px-4 py-3">
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={onClose}
                disabled={isSaving}
                className="flex-1 px-4 py-3 text-sm font-semibold text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 active:bg-gray-300 active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSaving || !title.trim() || !content.trim()}
                className="flex-1 px-4 py-3 text-sm font-semibold text-white bg-blue-600 rounded-xl hover:bg-blue-700 active:bg-blue-800 active:scale-[0.98] shadow-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                {isSaving ? 'Saving...' : editSnippet ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

